import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import AnalyticsService from '../services/AnalyticsService';
import { CustomerSearchResult } from '../services/models/analytics';

export interface CustomerValue {
  userId?: number;
  label?: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}

export const emptyCustomerValue: CustomerValue = { firstName: '', lastName: '', phone: '', email: '' };

const inputClass = 'w-full px-3 py-2 rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-20 bg-neutral-5 typo-small';

// Lets staff link a manual rental to an existing customer (search by name / email
// / phone) or fall back to typing a new customer's details.
const CustomerSelect: React.FC<{ value: CustomerValue; onChange: (v: CustomerValue) => void }> = ({ value, onChange }) => {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isFetching } = useQuery({
    queryKey: ['customer-search', debounced],
    queryFn: () => AnalyticsService.searchCustomers(debounced),
    enabled: debounced.length >= 2 && value.userId == null,
  });

  const results = data?.customers ?? [];

  const selectUser = (customer: CustomerSearchResult) => {
    onChange({
      userId: customer.userId,
      label: `${customer.firstName} ${customer.lastName}`.trim() || customer.email,
      firstName: customer.firstName,
      lastName: customer.lastName,
      phone: customer.phone,
      email: customer.email,
    });
    setSearch('');
    setOpen(false);
  };

  if (value.userId != null) {
    return (
      <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-primary-100 outline outline-1 outline-offset-[-1px] outline-primary-200">
        <div className="flex flex-col">
          <span className="typo-small text-neutral font-medium">{value.label}</span>
          {(value.email || value.phone) && (
            <span className="typo-small text-neutral-50">{[value.email, value.phone].filter(Boolean).join(' · ')}</span>
          )}
        </div>
        <button type="button" onClick={() => onChange(emptyCustomerValue)} className="p-1 rounded hover:bg-neutral-10" title="Retirer le client">
          <XMarkIcon className="h-4 w-4 text-neutral-40" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-40" />
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Rechercher un client existant"
          className={`${inputClass} pl-8`}
        />
        {open && debounced.length >= 2 && (
          <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-md bg-white shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10">
            {isFetching && <div className="px-3 py-2 typo-small text-neutral-40">Recherche…</div>}
            {!isFetching && results.length === 0 && <div className="px-3 py-2 typo-small text-neutral-40">Aucun client trouvé</div>}
            {results.map((customer) => (
              <button
                key={customer.userId}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectUser(customer)}
                className="w-full text-left px-3 py-2 hover:bg-neutral-5 transition-colors"
              >
                <div className="typo-small text-neutral">{`${customer.firstName} ${customer.lastName}`.trim() || customer.email}</div>
                <div className="typo-small text-neutral-50">{[customer.email, customer.phone].filter(Boolean).join(' · ')}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 text-neutral-40">
        <div className="h-px flex-grow bg-neutral-10" />
        <span className="typo-small">ou nouveau client</span>
        <div className="h-px flex-grow bg-neutral-10" />
      </div>

      <div className="flex items-center gap-2">
        <input type="text" value={value.firstName} onChange={(e) => onChange({ ...value, firstName: e.target.value })} placeholder="Prénom" className={inputClass} />
        <input type="text" value={value.lastName} onChange={(e) => onChange({ ...value, lastName: e.target.value })} placeholder="Nom" className={inputClass} />
      </div>
      <div className="flex items-center gap-2">
        <input type="tel" value={value.phone} onChange={(e) => onChange({ ...value, phone: e.target.value })} placeholder="Téléphone" className={inputClass} />
        <input type="email" value={value.email} onChange={(e) => onChange({ ...value, email: e.target.value })} placeholder="Email" className={inputClass} />
      </div>
    </div>
  );
};

export default CustomerSelect;
